/**
 * Sync logic pour le tracker dashboard.
 *
 * - syncOnce()   : un fetch /me/opens?since=cursor → merge dans le store
 * - startPolling : auto 30s en foreground, pause en background, refresh on focus
 * - stopPolling
 */

import { meOpens } from "../workerClient";
import { getAccessToken } from "../auth/oauth";
import { trackerState, mergeOpens } from "./store.svelte";

const POLL_INTERVAL_MS = 30 * 1000;
let pollTimer: ReturnType<typeof setInterval> | null = null;
let visibilityHandler: (() => void) | null = null;
let focusHandler: (() => void) | null = null;
let swMessageHandler: ((e: MessageEvent) => void) | null = null;

/** Un fetch + merge. Idempotent — appelable en concurrence sans souci. */
export async function syncOnce(): Promise<void> {
  if (trackerState.loading) return;
  trackerState.loading = true;
  trackerState.error = null;
  try {
    const token = await getAccessToken();
    if (!token) {
      // Pas signé in → on ne peut pas fetch, mais ce n'est pas une erreur (le
      // composant parent doit cacher la dashboard si pas signé in).
      return;
    }
    const r = await meOpens(token, trackerState.cursor, 100);
    mergeOpens(r.opens);
    trackerState.lastSyncAt = Date.now();
  } catch (e: unknown) {
    trackerState.error = e instanceof Error ? e.message : String(e);
  } finally {
    trackerState.loading = false;
  }
}

/** Démarre le polling (call une fois au mount du dashboard). */
export function startPolling(): void {
  // 1. Fetch immédiat
  void syncOnce();

  // 2. Polling 30s
  if (pollTimer === null) {
    pollTimer = setInterval(() => {
      // Skip si onglet caché (les push handle le live, pas besoin de polluer)
      if (document.visibilityState === "visible") void syncOnce();
    }, POLL_INTERVAL_MS);
  }

  // 3. Refresh au focus / retour onglet
  if (!visibilityHandler) {
    visibilityHandler = () => {
      if (document.visibilityState === "visible") void syncOnce();
    };
    document.addEventListener("visibilitychange", visibilityHandler);
  }
  if (!focusHandler) {
    focusHandler = () => void syncOnce();
    window.addEventListener("focus", focusHandler);
  }

  // 4. Sync immédiat sur push reçu en foreground (le SW message la page).
  //    Et si l'user a tap la notif, on ouvre la modale détail du pixel.
  if (!swMessageHandler && "serviceWorker" in navigator) {
    swMessageHandler = (e: MessageEvent) => {
      const data = e.data as { type?: string; pixelId?: string };
      if (data?.type === "open-pixel") {
        void syncOnce();
        if (data.pixelId) {
          trackerState.detailPixelId = data.pixelId;
        }
      }
    };
    navigator.serviceWorker.addEventListener("message", swMessageHandler);
  }
}

export function stopPolling(): void {
  if (pollTimer !== null) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
  if (visibilityHandler) {
    document.removeEventListener("visibilitychange", visibilityHandler);
    visibilityHandler = null;
  }
  if (focusHandler) {
    window.removeEventListener("focus", focusHandler);
    focusHandler = null;
  }
  if (swMessageHandler && "serviceWorker" in navigator) {
    navigator.serviceWorker.removeEventListener("message", swMessageHandler);
    swMessageHandler = null;
  }
}
